import { cn } from '@goaltree/ui/lib/utils'
import { Camera, Mic, MicOff, Phone, Video, VideoOff } from 'lucide-react'
import { useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { fmtCallTime, useCaptionsPref } from '@/components/coach-call'
import { useCoachCall } from '@/lib/coach-call-store'
import { getSelfCamera, setSelfCamera, subscribeSelfCamera } from '@/lib/self-camera'

const circle =
  'flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border transition-colors'

function circleClass(on: boolean) {
  return cn(
    circle,
    on
      ? 'border-foreground bg-foreground text-background'
      : 'border-border bg-card text-foreground hover:bg-muted',
  )
}

// Minimised call on a phone: one row just above the bottom-nav coach. Tap the
// face itself (the nav button) to come back to the full call.
export function CoachNavCallPill() {
  const call = useCoachCall((s) => s.navCall)
  const { t } = useTranslation()
  const [captions, toggleCaptions] = useCaptionsPref()
  const selfCam = useSyncExternalStore(subscribeSelfCamera, getSelfCamera, () => ({
    on: false,
    error: null as null,
  }))
  if (!call) return null
  const toggleMine = () => {
    const next = !getSelfCamera().on
    void setSelfCamera(next).then((ok) => {
      if (next && !ok) toast.error(t('callView.myCameraDenied'))
    })
  }

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-full z-10 mb-9 flex flex-col items-center gap-1.5 px-3">
      {captions && !call.muted && call.caption && (
        <p
          aria-live="polite"
          className="pointer-events-auto max-w-full rounded-xl bg-background/95 px-3 py-1.5 text-center text-xs leading-snug font-medium text-foreground shadow-md backdrop-blur-xl"
        >
          {call.caption}
        </p>
      )}
      <div
        role="region"
        aria-label={t('callView.pip.region', { name: call.name })}
        className="pointer-events-auto flex max-w-full items-center gap-1 rounded-full border border-border/70 bg-background/95 py-1 pr-1 pl-2.5 shadow-[0_8px_28px_rgba(0,0,0,0.28)] backdrop-blur-xl"
      >
        <span className="flex shrink-0 items-center gap-1.5 pr-1 text-[11px] font-medium whitespace-nowrap text-muted-foreground">
          {call.connecting ? (
            t('callView.connecting')
          ) : (
            <>
              <span className="size-1.5 rounded-full bg-red-500" />
              <span className="font-mono text-foreground tabular-nums">
                {fmtCallTime(call.elapsedSeconds)}
              </span>
              {call.coachSpeaking && (
                <span
                  aria-label={t('callView.speaking')}
                  role="img"
                  className="coach-audio-wave ml-0.5"
                >
                  {[0, 1, 2, 3, 4].map((bar) => (
                    <span key={bar} className="coach-audio-wave__bar" />
                  ))}
                </span>
              )}
            </>
          )}
        </span>
        <button
          type="button"
          onClick={toggleMine}
          aria-pressed={selfCam.on}
          aria-label={t(selfCam.on ? 'callView.myCameraOn' : 'callView.myCamera')}
          title={t(selfCam.on ? 'callView.myCameraOn' : 'callView.myCamera')}
          className={circleClass(selfCam.on)}
        >
          <Camera className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={toggleCaptions}
          aria-pressed={captions}
          aria-label={t('callView.captions')}
          title={t('callView.captions')}
          className={circleClass(captions)}
        >
          <span className="text-[10px] leading-none font-extrabold">CC</span>
        </button>
        {call.canVideo && (
          <button
            type="button"
            onClick={call.toggleVideo}
            aria-pressed={call.videoOn}
            aria-label={t(call.videoOn ? 'callView.videoOff' : 'callView.videoOn')}
            title={t(call.videoOn ? 'callView.videoIsOn' : 'callView.videoIsOff')}
            className={circleClass(call.videoOn)}
          >
            {call.videoOn ? <Video className="size-3.5" /> : <VideoOff className="size-3.5" />}
          </button>
        )}
        <button
          type="button"
          onClick={call.toggleMute}
          aria-pressed={call.muted}
          aria-label={t(call.muted ? 'coach.unmute' : 'coach.mute')}
          title={t(call.muted ? 'coach.unmute' : 'coach.mute')}
          className={circleClass(call.muted)}
        >
          {call.muted ? <MicOff className="size-3.5" /> : <Mic className="size-3.5" />}
        </button>
        <button
          type="button"
          onClick={call.hangUp}
          aria-label={t('coach.callEnd')}
          title={t('coach.callEnd')}
          className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-red-500 text-white transition-colors hover:bg-red-600"
        >
          <Phone className="size-3.5 rotate-[135deg]" fill="currentColor" />
        </button>
      </div>
    </div>
  )
}
